/**
 * Campaign content builders — the shared toolkit every world file imports.
 *
 * This is the LEAF module of the campaign content graph: it depends only on
 * pvp/species primitives, never on campaignConfig.ts or any world file, so the
 * per-world content files (world1.ts … world10.ts) can import `node`/`rival`
 * here without creating an import cycle. campaignConfig.ts re-exports everything
 * below, so existing `import { … } from './campaignConfig.js'` call sites keep
 * working unchanged.
 *
 * What lives here: the campaign content TYPES, the difficulty-band and XP
 * constants, the species-ID constants used in squads, and the terse `node()` /
 * `rival()` builders. The difficulty MATH (stamping rival levels/caps from the
 * bands), progression helpers, and validation stay in campaignConfig.ts.
 */

import { COMBAT_MAX_LEVEL } from '../pvpConstants.js';
import { SUIKAMON_SPECIES } from '../speciesConfig.js';
import { tracksForGift } from '../suikamonTracks.js';
import { getSpeciesSlug } from '../petHelper.js';
// Type-only (runtime-erased) — keeps this module the leaf of the content graph.
import type { NodeDialogue } from './rivalMemory.js';

// ============================================================================
// Level Bands — the ONLY source of truth for campaign difficulty.
// Change these three numbers and every rival level, finale and player cap rescale
// automatically (see stampWorldRivalLevels() in campaignConfig.ts). With the
// defaults, world N (0-indexed) covers the level band
// [WORLD_1_START_LEVEL + 10N … WORLD_1_FINAL_LEVEL + 10N], so the ten campaign
// worlds tile the full 1→100 range, ten levels per world. That is the AUTHORED
// band — COMBAT_MAX_LEVEL clamps what any of it is actually fought at, so a band
// above 20 buys rival STAGE and XP scaling, not a harder-hitting rival.
// ============================================================================

/**
 * Difficulty escalation step between worlds. World N's rival start/finale
 * levels (and player cap) are each WORLD_LEVEL_STEP higher than World N-1's.
 */
export const WORLD_LEVEL_STEP = 10;

/** World 1's first-node rival level. Later worlds add WORLD_LEVEL_STEP per world. */
export const WORLD_1_START_LEVEL = 1;

/** World 1's finale level. Later worlds add WORLD_LEVEL_STEP per world. */
export const WORLD_1_FINAL_LEVEL = 10;

/**
 * In a world's FINALE node, the boss (squad slot 0) fights at the world's finale
 * level; its companions fight this many levels below it.
 *
 * **WIDENING THIS WAS TRIED, TWICE, AND REVERTED BOTH TIMES.** The gauntlet's G1 reads boss
 * retries at the 90th percentile as **3 against an allowance of 1**, and its named knob is
 * "boss levels" — which is INERT here, because the player meets every node at parity with its
 * lead opponent, so lowering a boss's level lowers the player's with it. The gap between the
 * boss and its escort is the one boss lever that is not inert: the player arrives at the BOSS's
 * level, so a wider gap leaves the escort genuinely below them.
 *
 * At 5 it looked worth 5 points of campaign clear rate — but that reading was taken while the
 * Common printing was still weak, and once `COMMON_TIER_VALUE_SHARE` was raised to pay for the
 * per-side piles the gap bought **exactly nothing** (78.9% at a gap of 2, 78.9% at 5, 76.3% at
 * 8). Boss retries p90 stayed at 3 throughout. A live change to the campaign's difficulty curve
 * that buys no measurable improvement should not ship, so this is the number it always was.
 */
export const FINALE_BOSS_COMPANION_GAP = 2;

/**
 * **HOW MANY LEVELS A MID-WORLD BOSS STANDS ABOVE THE TRAIL AROUND IT.**
 *
 * `isBossNode` covers `mini_boss` and `zone_boss` as well as the world finale, and the map, the
 * UI and the dialogue all treat the mark as the chapter's wall — but nothing in the level ramp
 * used to act on it. Every non-finale node, boss or not, was stamped at the same linear point
 * between the world's start and its finale, so **32 of the campaign's 42 bosses were a normal
 * fight wearing a badge**.
 *
 * `campaign-gauntlet.ts` measured exactly that: a boss was **+1.1 ±2.5 points** harder than its
 * own world's trail nodes, against a design target of +5 (criterion N3).
 *
 * **THREE, AND THE NUMBER IS SWEPT AGAINST BOTH HARNESSES.** It was TWO, chosen in tuning log
 * row 29 against a campaign where a trail rival was forbidden the evolution ladder
 * (`CAMPAIGN_EVOLUTION_CAP` was 1/2/3). That cap was doing part of this constant's job: a boss
 * was a wall partly because it was the only fighter allowed to evolve. Retiring the cap handed
 * the whole job back here, and the number was re-swept:
 *
 * | bonus | N3 boss margin | N1 easiest | G1 boss retries p90 | G2 buying helps |
 * |---|---|---|---|---|
 * | 2 | +2.9 ±2.7 ≈ | 72.1% ✓ | 3 | 38.2% ✗ |
 * | **3** | **+4.7 ≈ at 90 attempts, UNSTABLE at 717k** | **72.0% ✓** | **2** | **37.7% ✗** |
 * | 4 | UNSTABLE | 71.6% ✓ | — | — |
 *
 * **3 improves the boss margin, the retry count AND G2 together**, which is not the trade row 29
 * measured — there, 3 pushed G2 under its floor. The context is different: G2 now sits above its
 * CEILING rather than below its floor, so harder bosses move it the helpful way. Nothing was
 * pushed out of band to buy it.
 *
 * **4 is still the ceiling**, exactly as row 29 found: N3 goes UNSTABLE between seeds, and a
 * verdict that flips on the dice is not a verdict to tune toward (R5).
 *
 * **N3 IS UNRESOLVED, not passing, and saying so is the point.** The +4.7 read at 90 attempts
 * did not survive confirmation: at **717,250 node attempts** across both seeds it reads UNSTABLE
 * (1337 inconclusive, 42 fail), where bonus 2 read a stable +2.9 ±2.7 INCONCLUSIVE. Neither is a
 * pass. Per R5 an UNSTABLE verdict is a sample-size problem and never a reason to turn this back,
 * so 3 is kept on the criteria that DID resolve — N1 and N4 confirmed at the high sample, G1
 * retries 3 → 2, G2 38.2% → 37.7%, and nothing moved the wrong way.
 *
 * What can be said either way is that **a boss is at best only just the wall of its world**, and
 * that was true before the evolution work too (+6.8 ±1.7 against a +5.0 floor). This constant is
 * the knob if that has to change — after raising `--attempts` far enough to get a verdict.
 *
 * **CLAMPED TO THE WORLD'S FINALE LEVEL**, never above it. The finale is the top of the band and
 * the player's own cap is derived from it (`getWorldPlayerCap`), so a mid-world boss allowed past
 * it would be a fight the campaign authorises and then refuses to let the player prepare for —
 * the failure criterion N7 exists to catch, and the reason this is a `Math.min` and not a sum.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * **IT IS 0 NOW, AND WHAT REPLACED IT IS THE THING THIS WAS ALWAYS A PROXY FOR.**
 *
 * This existed because the boss MARK was decoration: a boss fought at the trail's level with the
 * trail's kit, so three levels were bolted on to make it stand up. A boss is now dealt a **fixed
 * hand that reaches the top of the evolution ladder** (`CAMPAIGN_FIXED_EVOLUTION_HAND`), which is
 * a far stronger and far more legible escalation — the player watches the chip climb — and
 * measured, it made the boss **the hardest node in 100% of worlds** (it was 40–50%).
 *
 * Stacked on top of each other the two were a wall: the hardest node's win rate read **14%**
 * against `N1`'s 30% floor, the steepest step between two nodes **−41 points** against `N4`'s 25,
 * and the run half's clear rate collapsed. Removing the levels keeps the escalation and gives back
 * the fight — see Row 44. **A boss stands above its trail through the ladder now, not through a
 * number the player cannot see.**
 */
export const BOSS_NODE_LEVEL_BONUS = 0;

/**
 * The player's squad is capped this many levels BELOW the world's finale.
 * Set to 0 — the cap now simply equals the world's finale level (no
 * artificial under-leveling), keeping early worlds approachable for new
 * players; difficulty is carried by opponent count + level ramp instead.
 */
export const CAMPAIGN_CAP_BELOW_FINAL_BOSS = 0;

/** WORLD-1 base XP granted to each squad pet on FIRST clear of a normal node */
export const CAMPAIGN_XP_NORMAL = 25;

/** WORLD-1 base XP granted to each squad pet on FIRST clear of a mini-boss node */
export const CAMPAIGN_XP_MINI_BOSS = 40;

/** WORLD-1 base XP granted to each squad pet on FIRST clear of a zone-boss node */
export const CAMPAIGN_XP_ZONE_BOSS = 50;

/**
 * How much the first-clear XP multiplier grows per world: World N (0-indexed)
 * pays baseXp × (1 + N × this step) — with step 1, World 1 pays ×1 and World 10
 * pays ×10 (see stampWorldXpRewards() in campaignConfig.ts). Keeps late worlds
 * worth their rising difficulty against the flat XP_PER_LEVEL = 1000 level cost,
 * the same one-knob pattern as the level bands above.
 */
export const CAMPAIGN_XP_WORLD_MULTIPLIER_STEP = 1;

/**
 * Rival of the Day: bonus XP granted to EACH squad pet the first time the
 * player finishes today's daily rematch with a WIN (on top of the normal
 * repeat-node XP). Once per UTC day (daily_rival_claims UNIQUE guard).
 */
export const DAILY_RIVAL_XP_WIN = 40;

/**
 * Rival of the Day consolation: bonus XP granted to EACH squad pet when the
 * daily rematch is LOST — attempting the challenge is never worthless.
 */
export const DAILY_RIVAL_XP_LOSS = 10;

/**
 * Rival of the Day difficulty: the daily rematch scales to the player's own strength — the
 * rival matches the player's best (highest-level) Suikamon — but never above this cap, so it
 * stays a fair, quick daily challenge regardless of world. Overrides the node's own level cap
 * for the daily rival only.
 */
export const DAILY_RIVAL_LEVEL_CAP = 20;

// ============================================================================
// Types
// ============================================================================

/**
 * One pet in a rival trainer's squad. `level` and `stage` are NOT authored —
 * they are stamped at module load by stampWorldRivalLevels() from the pet's
 * world level band and the node's position in the world (stage derived from
 * level). Squads only author species, nicknames, and slot order (slot 0 is the
 * boss in a finale node).
 */
export interface CampaignRivalPet {
  speciesId: number;
  level: number;
  stage: number;
  /** Optional flavor nickname shown instead of the species name. */
  nickname?: string;
}

export type CampaignNodeKind = 'normal' | 'mini_boss' | 'zone_boss' | 'gift';

// ============================================================================
// Gift nodes — reward-only steps in the journey ("Trailside Gift")
// ============================================================================

/**
 * The reward a GIFT node hands the player on its FIRST clear (no battle). A
 * flexible bundle — a node authors whichever combination fits. Suika and XP are
 * granted to EACH combat-capable owned pet (a gift has no squad selection);
 * Soul Dust is account-level (per species). First-time-only: replays never
 * re-grant (see claimCampaignReward + campaign_progress idempotency).
 */
export interface CampaignReward {
  /**
   * Suika into the player's wallet. Only a world's Soul Token pays it, `GIFT_SKILL_SUIKA`: the
   * skill top-up that was paid in Scrolls until Beta 21.52 (`cardShopConfig.ts`).
   */
  suika?: number;
  /** XP granted to each combat-capable owned pet. */
  xp?: number;
  /**
   * **SOUL SHARDS** (`tracks` in code) of one species, toward finding its egg — the headline of
   * every shard gift. When the player already owns that species they arrive as Suika instead,
   * at the gift rate (`GIFT_SUIKA_MULTIPLIER`), so the gift is never wasted.
   */
  tracks?: { speciesId: number; amount: number };
}

/**
 * Gift reward amounts. XP mirrors battle-XP scaling (×world) so a Training Charm stays worth
 * roughly one battle. A Soul Token adds a flat Suika top-up toward skills. Soul Shards —
 * the gift's headline — are sized by the species' rarity in `suikamonTracks.ts` (`TRACKS_GIFT`).
 *
 * Gifts paid 10-55 Suika until 2026-10-04: a rounding error next to a day's login (~100) and a
 * card copy (1000), so opening one was a shrug. That Suika is gone; a gift is now Soul Shards.
 */
export const GIFT_XP_BASE = 20;

/**
 * The Soul Shards a gift of `speciesId` pays — `big` for a world's Soul Token. Sized by rarity
 * (`tracksForGift`), so a Legendary's gift is a step and a Common's is most of an egg.
 */
export function giftTracks(speciesId: number, big = false): { speciesId: number; amount: number } {
  const species = SUIKAMON_SPECIES.find((s) => s.id === speciesId);
  if (!species) throw new Error(`giftTracks: unknown species ${speciesId}`);
  return { speciesId, amount: tracksForGift(species, big) };
}

/** XP a gift grants in the world at `worldIndex` (0-based): 20 → 200, matching battle-XP scaling (×N). */
export function giftXp(worldIndex: number): number {
  return GIFT_XP_BASE * (worldIndex + 1);
}

/**
 * Who the player faces at a node:
 *   • 'trainer' (default) — a human/cast rival who speaks their own dialogue.
 *   • 'wild'    — a wild Suikamon encounter: no trainer, the narrator tells the
 *                 whole scene, the intro reads "A wild <Species> appeared!", and
 *                 the node's authored dialogue is written as narration. Combat,
 *                 XP, levels, and squad building are IDENTICAL to a trainer node —
 *                 this flag is purely presentation + light "farm-the-trail"
 *                 flavour between the story fights.
 */
export type CampaignEncounterKind = 'trainer' | 'wild';

/** Whether a node's intro and outcome lines are told by the narrator (a wild encounter, or `narrated`). */
export const isNarratedNode = (n: { encounterKind: CampaignEncounterKind; narrated?: true }): boolean =>
  n.encounterKind === 'wild' || n.narrated === true;

export interface CampaignNode {
  /** Permanent unique ID, e.g. 'z1-n1'. Never change after release. */
  nodeId: string;
  kind: CampaignNodeKind;
  /** Trainer battle (default) vs a wild-Suikamon encounter — see CampaignEncounterKind. */
  encounterKind: CampaignEncounterKind;
  /**
   * The lines are NARRATION, told by the narrator rather than spoken under the rival's name: a
   * Guardian is a Suikamon, not a trainer, and does not talk. Always true for a wild encounter
   * (`isNarratedNode`); set it on a Guardian finale whose lines are written as narration.
   */
  narrated?: true;
  /**
   * The species whose SOUL this battle is about — set on the 19 "soul nodes"
   * (each world's finale + the featured mini-bosses), absent everywhere else.
   * Every species appears as a soul node exactly ONCE across the campaign
   * (validated), so the saga doubles as a 19-soul compendium. Reserved for the
   * future soul-shard economy (first clear grants that species' Soul Shard);
   * the backend does not pay it out yet — see
   * docs/superpowers/specs/campaign-soul-shards.md.
   */
  soulSpeciesId?: number;
  rivalName: string;
  rivalTitle: string;
  /**
   * Stable rival CHARACTER identity — stamped at module load from rivalName by
   * stampRivalIds() (campaignConfig.ts), so the same name is the same character
   * everywhere. Keys the campaign_encounters memory table; never re-derive
   * differently after release.
   */
  rivalId: string;
  /** One-line story beat: why this battle happens. Shown before the rival speaks. */
  story: string;
  /**
   * The rival's lines. Base intro/playerWin/playerLose are required; the
   * optional rematch/repeat variants (see NodeDialogue in rivalMemory.ts) are
   * selected from the player's per-rival battle history and fall back to the
   * base lines when unauthored.
   */
  dialogue: NodeDialogue;
  /** Exactly SQUAD_SIZE pets authored; only the first `opponentCount` fight. */
  squad: CampaignRivalPet[];
  /**
   * How many rivals actually fight in this node (1, 2, or 3). Ramps up across
   * each world (1v1 early, 2v2 mid, 3v3 late) so new players aren't thrown into
   * full squad battles immediately. The player's squad must match this count
   * exactly — AI count always mirrors player count, difficulty is carried by
   * level, not by numbers advantage.
   */
  opponentCount: 1 | 2 | 3;
  /** Player pets fight capped at this level (the world's finale − CAMPAIGN_CAP_BELOW_FINAL_BOSS). */
  playerLevelCap: number;
  /** XP granted to each squad pet on first clear. */
  xpReward: number;
  /** Species whose portrait represents the rival on the map. */
  portraitSpeciesId: number;
  /** Background scene image for the node, e.g. '/img/ray-background.png'. */
  backgroundImage: string;
  /**
   * Set on GIFT nodes only (kind === 'gift'): the reward handed over on first
   * clear. Absent on battle nodes, which reward XP (and soul shards) through the
   * battle-completion path instead. See CampaignReward.
   */
  reward?: CampaignReward;
}

export interface CampaignZone {
  zoneId: string;
  name: string;
  element: string;
  /** Short chapter text shown on the map under the zone header. */
  story: string;
  nodes: CampaignNode[];
}

/**
 * A world is one self-contained story chapter. Worlds with content unlock in
 * sequence — World N is playable once World N-1 is fully cleared (see
 * isWorldUnlocked in campaignConfig.ts). Locked worlds (no content yet)
 * render as "Coming Soon" previews and reject battle creation regardless of
 * progress.
 */
export interface CampaignWorld {
  worldId: string;
  name: string;
  element: string;
  /**
   * The world's "hero" Suikamon — the Warden the tale is built around. Drives the
   * "{Species}'s Tale" framing and poster/portrait art on the storyline selector
   * (especially for locked worlds, which have no squads to derive a boss from).
   */
  featuredSpeciesId: number;
  /** Short world tagline: the tale this path tells (or a teaser when locked). */
  story: string;
  locked: boolean;
  /** Chapters, in order. Empty for locked (coming soon) worlds. */
  zones: CampaignZone[];
  /**
   * A Tale built from a book (`story/`) whose later chapters are still drafts: how many battles
   * the finished book will have. Rival levels ramp over this many, so a chapter plays as hard now
   * as it will when the book is whole. Absent: the ramp runs over the battles the Tale has.
   */
  plannedBattles?: number;
  /**
   * The book's chapters still being written, by number: shown as sealed pages after the playable
   * chapters, nothing of them told. While any is listed the Tale has not reached its real finale,
   * so the finale's soul rule waits (`validateCampaignConfig`).
   */
  draftChapters?: number[];
}

// ============================================================================
// Content builders (keep node definitions terse and consistent)
// ============================================================================

/**
 * Build one rival squad pet. `level`/`stage` are placeholders here (0/1) and are
 * assigned per-world by stampWorldRivalLevels() once the worlds are assembled —
 * squads only declare species, nickname, and slot order (slot 0 = finale boss).
 */
export function rival(speciesId: number, nickname?: string): CampaignRivalPet {
  return { speciesId, level: 0, stage: 1, nickname };
}

/** Background image path for a species, e.g. '/img/ray-background.png'. */
export function speciesBackground(speciesId: number): string {
  return `/img/${getSpeciesSlug(speciesId, SUIKAMON_SPECIES)}-background.png`;
}

export interface NodeSpec {
  nodeId: string;
  kind: CampaignNodeKind;
  /** Omit for a normal trainer battle; set 'wild' for a wild-Suikamon encounter. */
  encounterKind?: CampaignEncounterKind;
  /** See CampaignNode.narrated. */
  narrated?: true;
  rivalName: string;
  rivalTitle: string;
  story: string;
  dialogue: CampaignNode['dialogue'];
  squad: CampaignRivalPet[];
  opponentCount: 1 | 2 | 3;
  xpReward: number;
  /**
   * The species that represents this battle: drives BOTH the map portrait AND
   * the dimmed scene background, so the two always agree with each other and with
   * the story beat. One source of truth — a node's art can never drift apart.
   */
  portraitSpeciesId: number;
  /** Set on soul nodes only — see CampaignNode.soulSpeciesId. */
  soulSpeciesId?: number;
}

/**
 * Assemble a node. Rival levels and `playerLevelCap` are placeholders here and are
 * overwritten per-world once the worlds are assembled: stampWorldRivalLevels()
 * assigns each rival's level from the world's band, then stampWorldPlayerCaps()
 * gives every node cap = (world's finale level) − CAMPAIGN_CAP_BELOW_FINAL_BOSS.
 * A capped player pet is NOT nerfed to a rival's level — it fights at the world
 * cap, and the finale always outlevels the player by that margin. That's the
 * challenge.
 */
export function node(spec: NodeSpec): CampaignNode {
  return {
    nodeId: spec.nodeId,
    kind: spec.kind,
    encounterKind: spec.encounterKind ?? 'trainer',
    ...(spec.narrated ? { narrated: true as const } : {}),
    rivalName: spec.rivalName,
    rivalTitle: spec.rivalTitle,
    rivalId: '', // stamped at load by stampRivalIds() in campaignConfig.ts
    story: spec.story,
    dialogue: spec.dialogue,
    squad: spec.squad,
    opponentCount: spec.opponentCount,
    playerLevelCap: COMBAT_MAX_LEVEL,
    xpReward: spec.xpReward,
    portraitSpeciesId: spec.portraitSpeciesId,
    // Background derives from the portrait species — one source of truth, so the
    // dimmed scene always matches the portrait shown (and the story about it).
    backgroundImage: speciesBackground(spec.portraitSpeciesId),
    soulSpeciesId: spec.soulSpeciesId,
  };
}

/**
 * One reward-only GIFT step in the journey. No battle: on first arrival the
 * player taps "Open Gift" once and receives `reward` (first-time-only via the
 * same campaign_progress idempotency battles use). Battle-only fields are inert
 * placeholders — an empty squad, opponentCount 1, no XP payout — so the pure
 * battle helpers skip it (guarded by isGiftNode) and the map renders a 🎁 card.
 * `giver`/`label` reuse the rivalName/rivalTitle slots as the gift's giver and
 * caption (e.g. giver 'Old Keeper', label 'Trailside Gift'); `portraitSpeciesId`
 * only sources the dimmed background scene (the card shows a gift glyph, not a
 * rival portrait).
 */
export interface GiftSpec {
  nodeId: string;
  /** The gift-giver's name — shown where a rival name would be. */
  giver: string;
  /** Short caption for the step, e.g. 'Trailside Gift'. */
  label: string;
  /** One-line story beat: why the gift is here. */
  story: string;
  /** The giver's single spoken line, shown on the claim card. */
  line: string;
  reward: CampaignReward;
  /** Species whose scene art dims behind the card. */
  portraitSpeciesId: number;
}

export function giftNode(spec: GiftSpec): CampaignNode {
  return {
    nodeId: spec.nodeId,
    kind: 'gift',
    encounterKind: 'trainer',
    rivalName: spec.giver,
    rivalTitle: spec.label,
    rivalId: '', // stamped at load by stampRivalIds() in campaignConfig.ts
    story: spec.story,
    // Minimal dialogue — the three base lines are required by NodeDialogue, but a
    // gift never battles, so all three carry the giver's single line.
    dialogue: { intro: spec.line, playerWin: spec.line, playerLose: spec.line },
    squad: [], // no battle — inert placeholder, skipped by isGiftNode-guarded helpers
    opponentCount: 1, // placeholder (never fought)
    playerLevelCap: COMBAT_MAX_LEVEL, // placeholder (unused for gifts)
    xpReward: 0,
    portraitSpeciesId: spec.portraitSpeciesId,
    backgroundImage: speciesBackground(spec.portraitSpeciesId),
    soulSpeciesId: undefined,
    reward: spec.reward,
  };
}

// ============================================================================
// Species IDs used in campaign squads (see speciesConfig.ts)
// ============================================================================

// Nature
export const BIREON = 0;
export const KOBU = 6;
export const PEFAREON = 8;
export const SUMIYA = 14;
export const PIKOTO = 16;
export const RAY = 17;
export const GORLY = 18;
// Water
export const AQUA = 1;
export const YETI = 4;
export const PINKY = 7;
export const SUIMON = 10;
export const FERNAIL = 13;
export const TRIXON = 15;
// Fire
export const HOTPAW = 3;
export const TALION = 9;
export const DRAGO = 12;
// Thunder
export const ZARICAT = 2;
export const TORO = 5;
export const VOLTARYX = 11;
